import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Box, Modal } from "@mui/material";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";
import sh from "../utils/sh";
import { incrementJobOfferMetric, incrementWebsiteClickCount } from "bundles/common/utils/utils";
import CompanyHeroSection from "bundles/common/components/company/CompanyHeroSection";
import CompanyTabsSection from "bundles/common/components/company/CompanyTabsSection";
import CompanyProfileTab from "bundles/common/components/company/CompanyProfileTab";
import CompanyHistoryTab from "bundles/common/components/company/CompanyHistoryTab";
import CompanyProjectsTab from "bundles/common/components/company/CompanyProjectsTab";
import CompanyJobsTab from "bundles/common/components/company/CompanyJobsTab";
import BackdropScreen from "bundles/common/components/BackdropScreen";
import AuthenticationModal from "bundles/Candidate/components/AuthenticationModal";
import SpontaneousApplicationDialog from "bundles/Candidate/components/SpontaneousApplicationDialog";
import useFollowCompany from "bundles/common/hooks/useFollowCompany";

const CompanyView = () => {
  const useQuery = () => {
    const { search } = useLocation();

    return useMemo(() => new URLSearchParams(search), [search]);
  };

  const query = useQuery();
  const params = useParams();
  const i18nLocale = useSelector((state) => state.i18nLocale);

  const currentCandidate = useSelector((state) => state.current_candidate);
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const authenticityToken = useSelector((state) => state.authenticity_token);

  const [company, setCompany] = useState({});
  const [jobOffers, setJobOffers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [jobCount, setJobCount] = useState(0);
  const [authenticationScope, setAuthenticationScope] = useState(null);
  const [isLoginModalOpened, setIsLoginModalOpened] = useState(false);
  const [isSpontaneous, setIsSpontaneous] = useState(false);
  const [loginToAddFavorite, setLoginToAddFavorite] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isApplying, setIsApplying] = useState(
    query.get("isApplying") === "true" || false
  );
  const [handleJob, setHandleJob] = useState(false);
  const [tabActive, setTabActive] = useState(
    query.get("selectedTab") || "profile"
  );
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();

  const { isFollowed: isCompanyFollowed, toggleFollow } = useFollowCompany();
  const isFollowed = isCompanyFollowed(company?.id);
  const handleFollowToggle = useCallback(() => toggleFollow(company?.id), [toggleFollow, company?.id]);

  const fetchCompany = () => {
    setLoading(true);
    sh.get(
      `${i18nLocale}/company/${params?.id || params?.company_id || currentRecruiter?.company?.id}`
    )
      .then((res) => {
        setCompany(res.data);
        setJobOffers(
          res?.data?.job_offers
            ?.filter(
              (j) => j.offer_status == "online" || j.offer_status == "closed"
            )
            .sort((a, b) => {
              if (a.offer_status < b.offer_status) return 1;
              if (a.offer_status > b.offer_status) return -1;
              if (a.published_at < b.published_at) return 1;
              if (a.published_at > b.published_at) return -1;
              return 0;
            })
        );
        setJobCount(
          res?.data?.job_offers?.filter((j) => j.offer_status == "online")
            ?.length || 0
        );
        setProjects(res.data.projects);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchCompany();
  }, []);

  const jobApplications = currentCandidate?.job_applications?.find(
    (jobApplication) =>
      !jobApplication.job_offer_id && jobApplication.company_id == company.id
  );

  const hasSpontaneousApplication = Boolean(jobApplications);

  const handleSpontaneousApplyClick = () => {
    if (currentCandidate?.id) {
      setIsApplying(true);
    } else {
      setAuthenticationScope(`spontaneous_${company.id}_S`);
      setIsSpontaneous(true);
      setIsLoginModalOpened(true);
    }
  };

  function handleChangeTab(event, newValue) {
    setTabActive(newValue);
  }

  const onLoginModalCloses = () => {
    let newURL = window.location.origin + window.location.pathname;

    if (authenticationScope?.includes("spontaneous")) {
      newURL += "?isApplying=true";
    } else if (authenticationScope?.includes("application")) {
      newURL += "?selectedTab=jobs";
    } else {
      newURL += window.location.search;
    }

    window.location.href = newURL;
  };


  useEffect(() => {
    if (!selectedJob) {
      setSelectedJob(jobOffers?.length > 0 ? jobOffers[0] : null);
    }
  }, [jobOffers?.length]);

  useEffect(() => {
    if (selectedJob?.id) {
      incrementJobOfferMetric(selectedJob, "view_count", authenticityToken);
    }
  }, [selectedJob?.id]);

  useEffect(() => {
    if (query.get("jid")) {
      sh.get(`/${i18nLocale}/candidate/job_offers/${query.get("jid")}`, {
        authenticity_token: authenticityToken,
      })
        .then((res) => {
          if (res?.data?.data) setSelectedJob(res?.data?.data);
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {});
    }
  }, [jobOffers?.length]);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "hsl(0, 0%, 100%)" }}>
      <CompanyHeroSection
        company={company}
        onSpontaneousApplyClick={handleSpontaneousApplyClick}
        hasSpontaneousApplication={hasSpontaneousApplication}
        isRecruiter={Boolean(currentRecruiter)}
        isFollowed={isFollowed}
        onFollowToggle={currentCandidate?.id ? handleFollowToggle : null}
      />

      {loading && <BackdropScreen open={loading} />}

      <CompanyTabsSection
        company={company}
        tabActive={tabActive}
        onChange={handleChangeTab}
      >
        {tabActive === "profile" && (
          <CompanyProfileTab company={company} onChange={handleChangeTab} />
        )}
        {tabActive === "history" && (
          <CompanyHistoryTab
            milestones={company?.company_milestones || []}
            companyName={company?.name}
          />
        )}
        {tabActive === "projects" && (
          <CompanyProjectsTab projects={company?.projects || []} />
        )}
        {tabActive === "jobs" && (
          <CompanyJobsTab jobOffers={company?.job_offers || []} />
        )}
      </CompanyTabsSection>

      <Modal
        open={isApplying && Boolean(currentCandidate?.id)}
        onClose={() => setIsApplying(false)}
        sx={{ display: "flex", alignItems: "center" }}
      >
        <SpontaneousApplicationDialog
          companyId={company?.id}
          closeModal={() => setIsApplying(false)}
          jobApplication={jobApplications}
        />
      </Modal>

      <AuthenticationModal
        isOpen={isLoginModalOpened}
        section={"login_or_signin"}
        closeModal={onLoginModalCloses}
        singleJob={true}
        customScope={authenticationScope}
        isSpontaneous={isSpontaneous}
        addFavoriteLogin={loginToAddFavorite}
      />
    </Box>
  );
};

export default CompanyView;
